import { columnResizingPluginKey } from '@tiptap/pm/tables';
import { parseStyleString } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
import { buildColStyleObject, getResponsiveWidthsByColumn, setResponsiveColWidthValue } from './utilities/responsiveColWidth';
function setStyleObject(el, styleObj) {
    for (let i = el.style.length - 1; i >= 0; i -= 1) {
        const propName = el.style.item(i);
        if (propName) {
            el.style.removeProperty(propName);
        }
    }
    Object.entries(styleObj).forEach(([property, value]) => {
        el.style.setProperty(property, value);
    });
}
function getBreakpointFromDOM(el) {
    let cursor = el;
    while (cursor) {
        const breakpointClass = Array.from(cursor.classList).find(cls => cls.startsWith('vp-') && cls.length > 3);
        if (breakpointClass) {
            return breakpointClass.slice(3);
        }
        cursor = cursor.parentElement;
    }
    return 'lg';
}
function getResponsivePrefixFromStyle(style) {
    const styleObj = parseStyleString(style || '');
    const keys = Object.keys(styleObj);
    for (const key of keys) {
        const match = key.match(/^--(.+)-table-col-width-[^-]+-\d+$/);
        if (match && match[1]) {
            return match[1];
        }
    }
    return 'vp';
}
function isEditableTableContext(el) {
    const host = el.closest('.ProseMirror');
    if (!host)
        return false;
    return host.getAttribute('contenteditable') === 'true';
}
function isColumnResizeDragging(view) {
    if (!view)
        return false;
    const resizeState = columnResizingPluginKey.getState(view.state);
    return !!(resizeState === null || resizeState === void 0 ? void 0 : resizeState.dragging);
}
function deferMicrotask(cb) {
    if (typeof queueMicrotask === 'function') {
        queueMicrotask(cb);
        return;
    }
    Promise.resolve().then(cb);
}
function getRowColWidths(node) {
    const row = node.firstChild;
    if (!row)
        return [];
    const widths = [];
    for (let i = 0, col = 0; i < row.childCount; i += 1) {
        const { colspan, colwidth } = row.child(i).attrs;
        for (let j = 0; j < colspan; j += 1, col += 1) {
            widths[col] = (colwidth && typeof colwidth[j] === 'number') ? colwidth[j] : null;
        }
    }
    return widths;
}
function didColWidthsChange(prevNode, nextNode) {
    const prevWidths = getRowColWidths(prevNode);
    const nextWidths = getRowColWidths(nextNode);
    if (prevWidths.length !== nextWidths.length)
        return true;
    for (let i = 0; i < prevWidths.length; i += 1) {
        if ((prevWidths[i] || null) !== (nextWidths[i] || null)) {
            return true;
        }
    }
    return false;
}
function findNodePosByRef(doc, target) {
    let found = null;
    doc.descendants((node, pos) => {
        if (node === target) {
            found = pos;
            return false;
        }
        return true;
    });
    return found;
}
export function updateColumns(node, colgroup, // <colgroup> has the same prototype as <col>
table, cellMinWidth, overrideCol, overrideValue) {
    var _a;
    let totalWidth = 0;
    let fixedWidth = true;
    let nextDOM = colgroup.firstChild;
    const row = node.firstChild;
    const editable = isEditableTableContext(table);
    const tableStyleString = typeof node.attrs.style === 'string' ? node.attrs.style : '';
    const responsiveWidthsByColumn = getResponsiveWidthsByColumn(tableStyleString);
    // Keep default min width available for PM-compatible cell min-width CSS in edit mode.
    table.style.setProperty('--default-cell-min-width', `${cellMinWidth}px`);
    if (row !== null) {
        for (let i = 0, col = 0; i < row.childCount; i += 1) {
            const { colspan, colwidth } = row.child(i).attrs;
            for (let j = 0; j < colspan; j += 1, col += 1) {
                const rawWidth = overrideCol === col ? overrideValue : (colwidth && colwidth[j]);
                const hasWidth = typeof rawWidth === 'number' && Number.isFinite(rawWidth) && rawWidth > 0
                    ? Math.max(rawWidth, cellMinWidth)
                    : undefined;
                const responsiveWidths = responsiveWidthsByColumn.get(col);
                totalWidth += hasWidth || cellMinWidth;
                if (!hasWidth) {
                    fixedWidth = false;
                }
                const colStyles = editable
                    ? (hasWidth ? { width: `${hasWidth}px` } : {})
                    : buildColStyleObject(cellMinWidth, hasWidth, responsiveWidths, {
                        domElement: table,
                    });
                if (!nextDOM) {
                    const colElement = document.createElement('col');
                    setStyleObject(colElement, colStyles);
                    colgroup.appendChild(colElement);
                }
                else {
                    setStyleObject(nextDOM, colStyles);
                    nextDOM = nextDOM.nextSibling;
                }
            }
        }
    }
    while (nextDOM) {
        const after = nextDOM.nextSibling;
        (_a = nextDOM.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(nextDOM);
        nextDOM = after;
    }
    // Keep PM default behavior in editable mode so live drag width and final width use identical layout rules.
    if (fixedWidth) {
        table.style.width = `${totalWidth}px`;
        table.style.minWidth = '';
    }
    else {
        table.style.width = '';
        table.style.minWidth = `${totalWidth}px`;
    }
}
export class TableView {
    constructor(node, cellMinWidth, view) {
        this.node = node;
        this.cellMinWidth = cellMinWidth;
        this.view = view;
        this.pendingResponsiveSync = false;
        this.dom = document.createElement('div');
        this.dom.className = 'tableWrapper';
        this.table = this.dom.appendChild(document.createElement('table'));
        // Apply user styles to the table element
        if (node.attrs.style) {
            this.table.style.cssText = node.attrs.style;
        }
        this.colgroup = this.table.appendChild(document.createElement('colgroup'));
        updateColumns(node, this.colgroup, this.table, cellMinWidth);
        this.contentDOM = this.table.appendChild(document.createElement('tbody'));
    }
    scheduleResponsiveColSync() {
        if (this.pendingResponsiveSync)
            return;
        this.pendingResponsiveSync = true;
        deferMicrotask(() => {
            this.pendingResponsiveSync = false;
            if (!this.view || isColumnResizeDragging(this.view)) {
                if (this.view) {
                    this.scheduleResponsiveColSync();
                }
                return;
            }
            this.syncResponsiveColWidths();
        });
    }
    syncResponsiveColWidths() {
        var _a, _b, _c;
        if (!this.view)
            return;
        const node = this.node;
        const bp = getBreakpointFromDOM(this.table);
        const prefix = getResponsivePrefixFromStyle(((_a = node.attrs) === null || _a === void 0 ? void 0 : _a.style) || '');
        const colWidths = getRowColWidths(node);
        let nextStyle = ((_b = node.attrs) === null || _b === void 0 ? void 0 : _b.style) || '';
        colWidths.forEach((width, index) => {
            if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0)
                return;
            nextStyle = setResponsiveColWidthValue(nextStyle, index, `${Math.max(width, this.cellMinWidth)}px`, {
                breakpoint: bp,
                prefix,
                domElement: this.table,
            });
        });
        if ((nextStyle || '') !== (((_c = node.attrs) === null || _c === void 0 ? void 0 : _c.style) || '')) {
            const pos = findNodePosByRef(this.view.state.doc, node);
            if (pos != null) {
                const tr = this.view.state.tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    style: nextStyle || null,
                });
                if (tr.docChanged) {
                    this.view.dispatch(tr);
                }
            }
        }
    }
    update(node) {
        if (node.type !== this.node.type) {
            return false;
        }
        const prevNode = this.node;
        this.node = node;
        this.table.style.cssText = typeof node.attrs.style === 'string' ? node.attrs.style : '';
        updateColumns(node, this.colgroup, this.table, this.cellMinWidth);
        // Keep responsive column vars in table style when PM colwidth changes (e.g. drag resize).
        if (this.view && didColWidthsChange(prevNode, node)) {
            this.scheduleResponsiveColSync();
        }
        return true;
    }
    ignoreMutation(mutation) {
        const target = mutation.target;
        const isInsideWrapper = this.dom.contains(target);
        const isInsideContent = this.contentDOM.contains(target);
        if (isInsideWrapper && !isInsideContent) {
            if (mutation.type === 'attributes' || mutation.type === 'childList' || mutation.type === 'characterData') {
                return true;
            }
        }
        return false;
    }
}
